import type { StoreProduct, StoreProductListParams } from "@medusajs/types";
import {
  keepPreviousData,
  queryOptions,
  type UseQueryResult,
  useQuery,
} from "@tanstack/react-query";
import { z } from "zod";
import { medusa } from "#/lib/medusa";
import type { BundleStock } from "#/lib/medusa-types/bundles";
import {
  type Product,
  type ProductImage,
  projectProduct,
  projectProductImages,
} from "#/lib/medusa-types/catalog";
import { FacetMapSchema, type FacetMap } from "#/lib/shop-search";
import { useDefaultRegionId } from "#/queries/regions";

export type {
  Product,
  ProductImage as ProductImageRow,
} from "#/lib/medusa-types/catalog";

// Catalog + facets change only on reseed; prices/stock rarely within a browse
// session. A short stale window serves cached pages on nav/pagination/filter
// toggles without a background refetch on every mount.
const PRODUCTS_STALE_MS = 60 * 1000;

export type ProductFilters = {
  q?: string;
  limit?: number;
  offset?: number;
  categoryId?: string;
  categorySlug?: string;
  categorySlugs?: string[];
  brandId?: string;
  brandSlug?: string;
  brandSlugs?: string[];
  ids?: string[];
  handles?: string[];
  onSale?: boolean;
  // Medusa accepts `title`, `-created_at`, etc. Price ordering is computed and
  // unsupported by the ORM (`Trying to query by not existing property
  // ProductVariant.calculated_price`), so price sort is done client-side over
  // the current page in callers.
  order?: string;
  includeFacets?: boolean;
};

export type ProductListResponse = {
  items: Product[];
  total: number;
  facets: FacetMap | null;
};

// TODO(post-real-sync): `+catalog_extras.*` lands with the catalog-extras
// module + link; until then medusa returns nothing and `projectProduct` falls
// back to `metadata.*`.
const PRODUCT_FIELDS =
  "*variants,*variants.calculated_price,*variants.options,+variants.inventory_quantity,+variants.manage_inventory,*options,*options.values,*tags,*images,*categories,*collection,+collection_id,+metadata,+catalog_extras.*";

const PRODUCT_LIST_FIELDS =
  "id,title,subtitle,handle,thumbnail,description,+collection_id,+metadata,*images,*categories,*tags,*variants.calculated_price,+variants.inventory_quantity,+variants.manage_inventory,+variants.barcode,+catalog_extras.*";

export const productKeys = {
  all: ["storefront", "products"] as const,
  list: (regionId: string | null, filters: ProductFilters) =>
    ["storefront", "products", "list", regionId, filters] as const,
  detail: (regionId: string | null, slug: string) =>
    ["storefront", "products", "detail", regionId, slug] as const,
  images: (regionId: string | null, productId: string) =>
    ["storefront", "products", "images", regionId, productId] as const,
  bundleStock: (variantId: string) => ["bundle-stock", variantId] as const,
};

export function isBundleProduct(product: StoreProduct): boolean {
  return product.tags?.some((t) => t.value === "bundle") ?? false;
}

async function fetchBundleStock(variantId: string): Promise<BundleStock> {
  return medusa.client.fetch<BundleStock>(`/store/bundle-stock/${variantId}`);
}

export function useBundleStock(
  variantId: string | undefined,
  options: { enabled?: boolean } = {},
): UseQueryResult<BundleStock> {
  const callerEnabled = options.enabled ?? true;
  return useQuery({
    queryKey: productKeys.bundleStock(variantId ?? ""),
    queryFn: () => fetchBundleStock(variantId ?? ""),
    enabled: callerEnabled && !!variantId,
  });
}

async function resolveCategoryId(slug: string): Promise<string | null> {
  const { product_categories } = await medusa.store.category.list({
    handle: slug,
    limit: 1,
  });
  return product_categories[0]?.id ?? null;
}

async function resolveCategoryIds(slugs: string[]): Promise<string[]> {
  if (slugs.length === 0) return [];
  const { product_categories } = await medusa.store.category.list({
    handle: slugs,
    limit: slugs.length,
  });
  return product_categories.map((c) => c.id);
}

async function resolveCollectionId(slug: string): Promise<string | null> {
  const { collections } = await medusa.store.collection.list({
    handle: slug,
    limit: 1,
  });
  return collections[0]?.id ?? null;
}

const SearchResponse = z.object({
  product_ids: z.array(z.string()),
  count: z.number(),
  facets: FacetMapSchema.optional(),
});

type SearchQuery = {
  q?: string;
  limit: number;
  offset: number;
  category_id?: string[];
  collection_id?: string[];
  include_facets?: string;
  on_sale?: string;
};

async function fetchProducts(
  filters: ProductFilters,
  regionId: string | undefined,
): Promise<ProductListResponse> {
  const limit = filters.limit ?? 20;
  const offset = filters.offset ?? 0;

  const categoryIds: string[] = [];
  if (filters.categoryId) categoryIds.push(filters.categoryId);
  if (filters.categorySlug) {
    const id = await resolveCategoryId(filters.categorySlug);
    if (!id) return { items: [], total: 0, facets: null };
    categoryIds.push(id);
  }
  if (filters.categorySlugs && filters.categorySlugs.length > 0) {
    const ids = await resolveCategoryIds(filters.categorySlugs);
    if (ids.length === 0) return { items: [], total: 0, facets: null };
    categoryIds.push(...ids);
  }

  const collectionIds: string[] = [];
  if (filters.brandId) {
    collectionIds.push(filters.brandId);
  } else if (filters.brandSlugs && filters.brandSlugs.length > 0) {
    const ids = (
      await Promise.all(filters.brandSlugs.map(resolveCollectionId))
    ).filter((id): id is string => id !== null);
    if (ids.length === 0) return { items: [], total: 0, facets: null };
    collectionIds.push(...ids);
  } else if (filters.brandSlug) {
    const id = await resolveCollectionId(filters.brandSlug);
    if (!id) return { items: [], total: 0, facets: null };
    collectionIds.push(id);
  }

  const hasQuery =
    filters.q !== undefined && filters.q.trim().length > 0;
  const useSearch =
    hasQuery ||
    filters.includeFacets === true ||
    filters.onSale === true ||
    categoryIds.length > 0 ||
    collectionIds.length > 0;
  if (useSearch) {
    const searchQuery: SearchQuery = {
      limit,
      offset,
    };
    if (hasQuery) searchQuery.q = filters.q;
    if (categoryIds.length > 0) searchQuery.category_id = categoryIds;
    if (collectionIds.length > 0) searchQuery.collection_id = collectionIds;
    if (filters.includeFacets) searchQuery.include_facets = "1";
    if (filters.onSale) searchQuery.on_sale = "1";
    const raw = await medusa.client.fetch<unknown>("/store/product-search", {
      query: searchQuery,
    });
    const parsed = SearchResponse.parse(raw);
    const facets = parsed.facets ?? null;
    if (parsed.product_ids.length === 0) {
      return { items: [], total: parsed.count, facets };
    }

    const hydrateQuery: StoreProductListParams = {
      id: parsed.product_ids,
      limit: parsed.product_ids.length,
      fields: PRODUCT_LIST_FIELDS,
    };
    if (regionId) hydrateQuery.region_id = regionId;
    const { products } = await medusa.store.product.list(hydrateQuery);
    const rankIndex = new Map(parsed.product_ids.map((id, i) => [id, i]));
    const ordered = products
      .map(projectProduct)
      .sort(
        (a, b) =>
          (rankIndex.get(a.id) ?? Number.MAX_SAFE_INTEGER) -
          (rankIndex.get(b.id) ?? Number.MAX_SAFE_INTEGER),
      );
    const items = filters.onSale
      ? ordered.filter((p) => p.metadata.compareAtAmount != null)
      : ordered;
    return { items, total: parsed.count, facets };
  }

  const query: StoreProductListParams = {
    limit,
    offset,
    fields: PRODUCT_LIST_FIELDS,
  };
  if (regionId) query.region_id = regionId;
  if (filters.ids && filters.ids.length > 0) query.id = filters.ids;
  if (filters.handles && filters.handles.length > 0)
    query.handle = filters.handles;
  if (filters.order) query.order = filters.order;
  if (categoryIds.length > 0) query.category_id = categoryIds;
  if (collectionIds.length > 0) query.collection_id = collectionIds;

  const { products, count } = await medusa.store.product.list(query);
  let items = products.map(projectProduct);
  if (filters.handles && filters.handles.length > 0) {
    const rank = new Map(filters.handles.map((h, idx) => [h, idx]));
    items = [...items].sort(
      (a, b) =>
        (rank.get(a.handle ?? "") ?? Number.MAX_SAFE_INTEGER) -
        (rank.get(b.handle ?? "") ?? Number.MAX_SAFE_INTEGER),
    );
  }
  if (filters.onSale) {
    items = items.filter((p) => p.metadata.compareAtAmount != null);
  }
  return { items, total: count, facets: null };
}

async function fetchProductByHandle(
  handle: string,
  regionId: string | undefined,
): Promise<Product | null> {
  const query: StoreProductListParams = {
    handle,
    limit: 1,
    fields: PRODUCT_FIELDS,
  };
  if (regionId) query.region_id = regionId;
  const { products } = await medusa.store.product.list(query);
  const first = products[0];
  return first ? projectProduct(first) : null;
}

async function fetchProductImagesById(
  id: string,
  regionId: string | undefined,
): Promise<ProductImage[]> {
  const { product } = await medusa.store.product.retrieve(id, {
    fields: "*images",
    ...(regionId ? { region_id: regionId } : {}),
  });
  return projectProductImages(product);
}

export const productQueries = {
  list: (regionId: string | undefined, filters: ProductFilters) =>
    queryOptions({
      queryKey: productKeys.list(regionId ?? null, filters),
      queryFn: () => fetchProducts(filters, regionId),
      enabled: regionId !== undefined,
      staleTime: PRODUCTS_STALE_MS,
    }),
  detail: (regionId: string | undefined, slug: string) =>
    queryOptions({
      queryKey: productKeys.detail(regionId ?? null, slug),
      queryFn: () => fetchProductByHandle(slug, regionId),
      enabled: regionId !== undefined && slug.length > 0,
    }),
  images: (regionId: string | undefined, productId: string | undefined) =>
    queryOptions({
      queryKey: productKeys.images(regionId ?? null, productId ?? ""),
      queryFn: () => fetchProductImagesById(productId ?? "", regionId),
      enabled: !!productId,
    }),
};

export function useProducts(
  filters: ProductFilters = {},
  options: { enabled?: boolean; keepPreviousData?: boolean } = {},
): UseQueryResult<ProductListResponse> {
  const regionId = useDefaultRegionId();
  const callerEnabled = options.enabled ?? true;
  const queryOpts = productQueries.list(regionId, filters);
  return useQuery({
    ...queryOpts,
    enabled: queryOpts.enabled && callerEnabled,
    placeholderData: options.keepPreviousData ? keepPreviousData : undefined,
  });
}

export function useProduct(slug: string): UseQueryResult<Product | null> {
  const regionId = useDefaultRegionId();
  return useQuery(productQueries.detail(regionId, slug));
}

export function useProductImages(
  productId: string | undefined,
): UseQueryResult<ProductImage[]> {
  const regionId = useDefaultRegionId();
  return useQuery(productQueries.images(regionId, productId));
}

export function useFeaturedProducts(
  limit = 12,
): UseQueryResult<ProductListResponse> {
  return useProducts({ limit });
}

export function useSaleProducts(
  limit = 24,
): UseQueryResult<ProductListResponse> {
  return useProducts({ onSale: true, limit });
}
